import { useEffect, useState } from 'react';
import { Profile, PromptVersionControl } from '@Models/types';
import { MoonLoader } from 'react-spinners';
import PromptSection from '../PromptSection';
import { IoIosWarning } from 'react-icons/io';
import { Warning, CheckCircle } from '@phosphor-icons/react';
import { accountsAPI } from '@Lib/axios/account';
import { useAuth } from '@Context/CognitoAuthProvider';

interface Props {
  profile: Profile;
  prompts: PromptVersionControl[];
}

const AdminSettings: React.FC<Props> = ({ profile, prompts }) => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');

  const [globalPrompt, setGlobalPrompt] = useState<PromptVersionControl | null>(
    null
  );

  const { logout } = useAuth();
  const account = accountsAPI(logout);

  if (globalPrompt) {
    profile.prompt = globalPrompt.content;
  }

  useEffect(() => {
    account
      .getCurrentGlobalPrompt()
      .then((data) => {
        setGlobalPrompt(data);
      })
      .catch(() => {
        setMessage('Error fetching global prompt');
        setMessageType('error');
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex h-full w-full flex-col gap-3 p-2">
      <div className="flex flex-col">
        <h2 className="text-xl font-semibold text-blue-900">
          Manage Global Prompts
        </h2>
        <p className="text-sm text-blue-900">
          Here you can change the prompt and Open AI API Key used by all users.
        </p>
      </div>

      <div className="h-full">
        {loading ? (
          <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5 py-20">
            <MoonLoader size={75} color="#071D49" />
            <span className="animate-pulse text-sm font-semibold tracking-wide text-gray-600">
              Global Prompt Loading...
            </span>
          </span>
        ) : (
          <>
            {messageType === 'error' && (
              <div className="mb-3 flex items-center justify-center font-bold text-red-500">
                <Warning size={20} weight="bold" className="mr-2" />
                <span>{message}</span>
              </div>
            )}
            {messageType === 'success' && (
              <div className="mb-3 flex items-center justify-center font-bold text-green-500">
                <CheckCircle size={20} weight="bold" className="mr-2" />
                <span>{message}</span>
              </div>
            )}
            <div className="rounded-lg border border-gray-400">
              <PromptSection
                profile={profile}
                prompts={prompts}
                onDeployVersion={account.updateOpenAICreds}
                onAddPrompt={(email, prompt, version, created_by, nickname) =>
                  account.addPrompt(
                    email,
                    prompt,
                    version,
                    created_by,
                    false,
                    nickname
                  )
                }
              />
            </div>
          </>
        )}
      </div>

      <div className="mt-6 flex flex-col pb-5 text-red-600">
        <span className="flex flex-row items-center gap-1 text-xs font-bold sm:text-base">
          <IoIosWarning className="text-red-600" size="1rem" />
          Note:
        </span>
        <span className="mt-2 text-justify text-xs font-semibold sm:text-sm">
          The prompt you enter will be appended to a pre-determined user story
          and format to ensure consistency in the generated test cases. Please
          make sure to include{' '}
          <span className="font-bold">{'{user_story}'}</span> and{' '}
          <span className="font-bold">{'{test_collection}'}</span> placeholders.
        </span>
      </div>
    </div>
  );
};

export default AdminSettings;
